import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";
import { DiscountUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { cartPerformance } from "@theme/performance";

const STAFFEL_CODE_PATTERN = /^STAFFEL-\d+%-VANAF-\d+$/i;
const AUTO_DISCOUNT_SOURCE_ID = "cart-discount:auto";
const STAFFEL_LOG_PREFIX = "[staffel]";

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    "cartDiscountError",
    "cartDiscountErrorDiscountCode",
    "cartDiscountErrorShipping",
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {MutationObserver | null} */
  #cartObserver = null;

  #scheduleSync = () => {};

  #log(message, payload = undefined) {
    const normalizedPayload =
      payload === undefined
        ? undefined
        : JSON.parse(
            JSON.stringify(payload, (_, value) => {
              if (value instanceof Error) {
                return {
                  message: value.message,
                  stack: value.stack,
                };
              }

              return value;
            }),
          );

    if (payload === undefined) {
      console.log(`${STAFFEL_LOG_PREFIX} ${message}`);
      window.__staffelDebug = {
        ...(window.__staffelDebug || {}),
        lastMessage: message,
      };
      window.__staffelDebugHistory = [
        ...(window.__staffelDebugHistory || []),
        { message },
      ].slice(-50);
      return;
    }

    console.log(`${STAFFEL_LOG_PREFIX} ${message}`, normalizedPayload);
    window.__staffelDebug = {
      ...(window.__staffelDebug || {}),
      lastMessage: message,
      lastPayload: normalizedPayload,
    };
    window.__staffelDebugHistory = [
      ...(window.__staffelDebugHistory || []),
      { message, payload: normalizedPayload },
    ].slice(-50);
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  // Shopify Functions are now the source of truth for staffel discounts.
  // This component only handles manual discount-code apply/remove interactions.

  #getSectionMarkupFromEvent(event) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || !(event instanceof Event)) return null;

    if ("detail" in event && typeof event.detail === "object" && event.detail) {
      const detail = /** @type {any} */ (event.detail);
      return (
        detail?.data?.sections?.[sectionId] ??
        detail?.resource?.sections?.[sectionId] ??
        null
      );
    }

    return null;
  }

  async #syncAutomaticDiscounts(sectionMarkup = null) {
    if (typeof this.dataset.sectionId !== "string") return;

    try {
      const root = this.#getRootFromMarkup(sectionMarkup);
      if (!root) return;

      const existingDiscounts = this.#existingDiscounts(root);
      const desiredStaffelCodes = this.#desiredStaffelCodes(root);
      this.#log("sync root snapshot", {
        lineItemCount: root.querySelectorAll('[data-staffel-eligible="true"]')
          .length,
        existingDiscounts,
        desiredStaffelCodes,
      });
      const existingStaffelCodes = existingDiscounts.filter((code) =>
        STAFFEL_CODE_PATTERN.test(code),
      );
      const preservedCodes = existingDiscounts.filter(
        (code) => !STAFFEL_CODE_PATTERN.test(code),
      );

      if (
        this.#sameCodeSet(existingStaffelCodes, desiredStaffelCodes) &&
        existingDiscounts.length ===
          preservedCodes.length + desiredStaffelCodes.length
      ) {
        this.#log("discounts already in sync", {
          desiredStaffelCodes,
        });
        return;
      }

      this.#log("syncing discounts", {
        existingDiscounts,
        desiredStaffelCodes,
      });

      const data = await this.#updateDiscountCodes([
        ...preservedCodes,
        ...desiredStaffelCodes,
      ]);
      if (!data) return;

      this.#log("discount update response", {
        discountCodes: data.discount_codes,
      });

      document.dispatchEvent(
        new DiscountUpdateEvent(data, AUTO_DISCOUNT_SOURCE_ID),
      );
      morphSection(
        this.dataset.sectionId,
        data.sections[this.dataset.sectionId],
      );
    } catch (error) {
      this.#log("sync failed", {
        message: error instanceof Error ? error.message : String(error),
      });
      console.error(error);
    }
  }

  #getRootFromMarkup(sectionMarkup) {
    if (!sectionMarkup) {
      return (
        document.querySelector("cart-items-component") ??
        this.closest("cart-items-component") ??
        this.closest(".shopify-section") ??
        document
      );
    }

    const parsedHtml = new DOMParser().parseFromString(
      sectionMarkup,
      "text/html",
    );
    const sectionId = this.dataset.sectionId;

    return (
      parsedHtml.getElementById(`shopify-section-${sectionId}`) ??
      parsedHtml.body
    );
  }

  #observeCartChanges() {
    const cartRoot =
      document.querySelector("cart-items-component") ??
      this.closest("cart-items-component") ??
      this.closest(".shopify-section");

    if (!(cartRoot instanceof Node)) return;

    this.#cartObserver?.disconnect();
    this.#cartObserver = new MutationObserver((mutations) => {
      this.#log("cart observer mutation batch", {
        mutationCount: mutations.length,
      });
      const shouldSync = mutations.some((mutation) => {
        if (mutation.type === "attributes") {
          return (
            mutation.target instanceof HTMLElement &&
            mutation.target.matches("[data-quantity]")
          );
        }

        return (
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof HTMLElement &&
              (node.matches("[data-quantity], .cart-discount__pill") ||
                node.querySelector("[data-quantity], .cart-discount__pill")),
          ) ||
          Array.from(mutation.removedNodes).some(
            (node) =>
              node instanceof HTMLElement &&
              (node.matches("[data-quantity], .cart-discount__pill") ||
                node.querySelector("[data-quantity], .cart-discount__pill")),
          )
        );
      });

      if (shouldSync) {
        console.debug("[staffel] cart mutation detected, resyncing discounts");
        this.#scheduleSync();
      }
    });

    this.#cartObserver.observe(cartRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-quantity"],
    });

    this.#log("cart observer attached");
  }

  #desiredStaffelCodes(root) {
    const lineItems = root.querySelectorAll('[data-staffel-eligible="true"]');
    const codes = new Set();

    this.#log("evaluating staffel items", {
      count: lineItems.length,
    });

    for (const lineItem of lineItems) {
      if (!(lineItem instanceof HTMLElement)) continue;

      const quantity = Number.parseInt(lineItem.dataset.quantity ?? "", 10);
      const unitsPerBox = Number.parseInt(
        lineItem.dataset.unitsPerBox ?? "",
        10,
      );
      const discountPct1Box = lineItem.getAttribute("data-discount-pct-1-box");
      const discountPct3Box = lineItem.getAttribute("data-discount-pct-3-box");
      const discountPct5Box = lineItem.getAttribute("data-discount-pct-5-box");
      this.#log("line item data", {
        productId: lineItem.dataset.productId,
        quantity,
        unitsPerBox,
        discountPct1Box,
        discountPct3Box,
        discountPct5Box,
      });

      if (!Number.isFinite(quantity) || !Number.isFinite(unitsPerBox)) {
        this.#log("line item skipped: invalid quantity or units_per_box", {
          productId: lineItem.dataset.productId,
          quantity,
          unitsPerBox,
        });
        continue;
      }
      if (unitsPerBox <= 0 || quantity < unitsPerBox) {
        this.#log("line item skipped: below first box threshold", {
          productId: lineItem.dataset.productId,
          quantity,
          unitsPerBox,
        });
        continue;
      }
      if (quantity % unitsPerBox !== 0) {
        this.#log("line item skipped: not a full box multiple", {
          productId: lineItem.dataset.productId,
          quantity,
          unitsPerBox,
        });
        continue;
      }

      const boxes = quantity / unitsPerBox;
      const thresholds = [
        {
          minimumBoxes: 5,
          percentage: discountPct5Box,
        },
        {
          minimumBoxes: 3,
          percentage: discountPct3Box,
        },
        {
          minimumBoxes: 1,
          percentage: discountPct1Box,
        },
      ];

      const activeThreshold = thresholds.find(
        ({ minimumBoxes, percentage }) => {
          return Number(percentage) > 0 && boxes >= minimumBoxes;
        },
      );

      if (!activeThreshold) continue;

      const code = this.#buildStaffelCode(
        activeThreshold.percentage,
        unitsPerBox * activeThreshold.minimumBoxes,
      );
      codes.add(code);

      this.#log("calculated staffel code", {
        productId: lineItem.dataset.productId,
        quantity,
        unitsPerBox,
        boxes,
        code,
      });
    }

    return Array.from(codes);
  }

  #buildStaffelCode(percentage, thresholdQuantity) {
    return `STAFFEL-${Math.ceil(Number(percentage))}%-VANAF-${thresholdQuantity}`;
  }

  #sameCodeSet(left, right) {
    if (left.length !== right.length) return false;

    const leftSorted = [...left].sort();
    const rightSorted = [...right].sort();

    return leftSorted.every((code, index) => code === rightSorted[index]);
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (
      !(discountCode instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== "string"
    )
      return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add("hidden");
      cartDiscountErrorDiscountCode.classList.add("hidden");
      cartDiscountErrorShipping.classList.add("hidden");

      const data = await this.#updateDiscountCodes(
        [...existingDiscounts, discountCodeValue],
        abortController,
      );
      if (!data) return;

      if (
        data.discount_codes.find(
          (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return (
              discount.code === discountCodeValue &&
              discount.applicable === false
            );
          },
        )
      ) {
        discountCode.value = "";
        this.#handleDiscountError("discount_code");
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
      const section = parsedHtml.getElementById(
        `shopify-section-${this.dataset.sectionId}`,
      );
      const discountCodes =
        section?.querySelectorAll(".cart-discount__pill") || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement
              ? element.dataset.discountCode
              : null,
          )
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) =>
            existingDiscounts.includes(code),
          ) &&
          data.discount_codes.find(
            (
              /** @type {{ code: string; applicable: boolean; }} */ discount,
            ) => {
              return (
                discount.code === discountCodeValue &&
                discount.applicable === true
              );
            },
          )
        ) {
          this.#handleDiscountError("shipping");
          discountCode.value = "";
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent("discount-update:user-action", event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== "Enter") ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const pill = event.target.closest(".cart-discount__pill");
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const data = await this.#updateDiscountCodes(
        existingDiscounts,
        abortController,
      );
      if (!data) return;

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(
        this.dataset.sectionId,
        data.sections[this.dataset.sectionId],
      );
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;
    const target =
      type === "discount_code"
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping;
    cartDiscountError.classList.remove("hidden");
    target.classList.remove("hidden");
  }

  /**
   * Returns an array of existing discount codes.
   * @param {ParentNode} [root]
   * @returns {string[]}
   */
  #existingDiscounts(root = this) {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = root.querySelectorAll(".cart-discount__pill");
    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === "string"
      ) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  async #updateDiscountCodes(codes, abortController = null) {
    if (typeof this.dataset.sectionId !== "string") return null;

    this.#log("posting discount codes", { codes });

    const controller = abortController ?? this.#createAbortController();
    const config = fetchConfig("json", {
      body: JSON.stringify({
        discount: codes.join(","),
        sections: [this.dataset.sectionId],
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });
      this.#log("discount POST completed", {
        ok: response.ok,
        status: response.status,
        redirected: response.redirected,
        url: response.url,
      });

      return response.json();
    } catch (error) {
      this.#log("discount POST failed", {
        message: error instanceof Error ? error.message : String(error),
        codes,
      });
      throw error;
    }
  }
}

if (!customElements.get("cart-discount-component")) {
  customElements.define("cart-discount-component", CartDiscount);
}
